import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  getInitialBgStyle,
  getOverlayOpacity,
  saveBgStyle,
  pickRandomNext,
  type BgStyle,
} from "@/components/common/BackgroundSelector";

const AUTO_ROTATE_MIN = 30_000;
const AUTO_ROTATE_MAX = 60_000;

function randomInterval() {
  return AUTO_ROTATE_MIN + Math.random() * (AUTO_ROTATE_MAX - AUTO_ROTATE_MIN);
}

interface BackgroundContextValue {
  activeBg: BgStyle;
  layerA: BgStyle;
  layerB: BgStyle;
  activeLayer: "a" | "b";
  overlayOpacity: number;
  onBgChange: (style: BgStyle) => void;
}

const BackgroundContext = createContext<BackgroundContextValue | null>(null);

export function useBackground() {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error("useBackground must be used within BackgroundProvider");
  return ctx;
}

export function BackgroundProvider({ children }: { children: ReactNode }) {
  // Always start with "animated" to match SSR, then sync to user preference after hydration
  const [layerA, setLayerA] = useState<BgStyle>("animated");
  const [layerB, setLayerB] = useState<BgStyle>("animated");
  const [activeLayer, setActiveLayer] = useState<"a" | "b">("a");
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const manualRef = useRef(false);

  // Apply stored/random bg after hydration
  useEffect(() => {
    const preferred = getInitialBgStyle();
    if (preferred !== "animated") {
      setLayerA(preferred);
    }
  }, []);

  const activeBg = activeLayer === "a" ? layerA : layerB;
  const overlayOpacity = getOverlayOpacity(activeBg);
  const activeBgRef = useRef(activeBg);
  activeBgRef.current = activeBg;

  const startTransition = useCallback((to: BgStyle) => {
    if (to === activeBgRef.current) return;
    setActiveLayer((cur) => {
      if (cur === "a") {
        setLayerB(to);
        return "b";
      } else {
        setLayerA(to);
        return "a";
      }
    });
    saveBgStyle(to);
  }, []);

  useEffect(() => {
    const schedule = () => {
      timerRef.current = setTimeout(() => {
        if (!manualRef.current) {
          startTransition(pickRandomNext(activeBgRef.current));
        }
        manualRef.current = false;
        schedule();
      }, randomInterval());
    };
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [startTransition]);

  const onBgChange = useCallback(
    (style: BgStyle) => {
      manualRef.current = true;
      startTransition(style);
    },
    [startTransition]
  );

  return (
    <BackgroundContext.Provider value={{ activeBg, layerA, layerB, activeLayer, overlayOpacity, onBgChange }}>
      {children}
    </BackgroundContext.Provider>
  );
}
